import { FC, ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import LoadingPage from 'src/features/plugins/outlook/components/LoadingPage';
import OfficeConnectionError from 'src/features/plugins/outlook/components/OfficeConnectionError';

const OFFICE_INIT_TIMEOUT_MS = 20_000;

declare global {
  interface Window {
    _historyCache?: { replaceState: any; pushState: any };
    Office?: any;
  }

  namespace Office {
    interface Item {
      conversationId?: string;
      dateTimeCreated?: Date;
    }
  }
}

interface OfficeContextType {
  isOfficeReady?: boolean;
  mailboxItem?: Office.Item;
  threadId?: string;
  email?: string;
}

const OfficeContext = createContext<OfficeContextType>({
  isOfficeReady: undefined,
  mailboxItem: undefined,
  email: undefined,
});

export const useOffice = (): OfficeContextType => {
  return useContext(OfficeContext);
};

/**
 * Hook to check if Office context is available.
 * Returns true if Office API is loaded and context is accessible.
 * Use this to conditionally render Office-specific features.
 */
export const useIsOfficeAvailable = (): boolean => {
  if (typeof window === 'undefined') return false;
  return typeof Office !== 'undefined' && Office?.context !== undefined;
};

interface OfficeProviderProps {
  children: ReactNode;
}

// Provider for the Microsoft Office context that is used to access the Microsoft Office API.
export const OfficeProvider: FC<OfficeProviderProps> = ({ children }) => {
  const [isOfficeReady, setIsOfficeReady] = useState<boolean>(false);
  const [isOfficeTimedOut, setIsOfficeTimedOut] = useState<boolean>(false);
  const [mailboxItem, setMailboxItem] = useState<Office.Item | any | undefined>(undefined);
  const [email, setEmail] = useState<string>();
  const [threadId, setThreadId] = useState<string>();
  const mountTime = useRef(Date.now());

  const log = (message: string, context?: Record<string, unknown>) => {
    if (typeof window === 'undefined') return;
    datadogLogs.logger.info(message, {
      ...context,
      source: 'OfficeContext',
      elapsedMs: Date.now() - mountTime.current,
    });
  };

  const logError = (message: string, context?: Record<string, unknown>) => {
    if (typeof window === 'undefined') return;
    datadogLogs.logger.error(message, {
      ...context,
      source: 'OfficeContext',
      elapsedMs: Date.now() - mountTime.current,
    });
  };

  const resolveEmailFromMailbox = () => {
    log('[resolveEmail] starting email resolution');
    try {
      const userEmail = Office.context?.mailbox?.userProfile?.emailAddress;
      const getSharedProps = Office.context?.mailbox?.item?.getSharedPropertiesAsync;

      log('[resolveEmail] userProfile email', {
        userEmail: userEmail ?? 'undefined',
        hasGetSharedProps: !!getSharedProps,
      });

      if (!getSharedProps) {
        log('[resolveEmail] no getSharedPropertiesAsync, using userProfile email', { userEmail });
        setEmail(userEmail);
        return;
      }

      getSharedProps((result: any) => {
        const isFailed =
          Office.AsyncResultStatus?.Failed !== undefined
            ? result?.status === Office.AsyncResultStatus.Failed
            : result?.status === 'failed' || result?.status === 'Failed';

        log('[resolveEmail] getSharedPropertiesAsync result', {
          status: result?.status,
          isFailed,
          hasOwner: !!result?.value?.owner,
          owner: result?.value?.owner ?? 'undefined',
        });

        if (isFailed || !result?.value?.owner) {
          log('[resolveEmail] getSharedPropertiesAsync failed or no owner, falling back to userProfile email', {
            isFailed,
            hasOwner: !!result?.value?.owner,
            userEmail: userEmail ?? 'undefined',
          });
          setEmail(userEmail);
          return;
        }
        log('[resolveEmail] using shared mailbox owner as email', { owner: result.value.owner });
        setEmail(result.value.owner);
      });
    } catch (err) {
      const fallbackEmail = Office.context?.mailbox?.userProfile?.emailAddress;
      logError('[resolveEmail] caught error, falling back to userProfile email', {
        error: String(err),
        fallbackEmail: fallbackEmail ?? 'undefined',
      });
      setEmail(fallbackEmail);
    }
  };

  const itemChangedHandler = () => {
    log('[itemChanged] handler fired', {
      hasOffice: !!window.Office,
      hasContext: !!Office.context,
      hasMailbox: !!Office.context?.mailbox,
      hasItem: !!Office.context?.mailbox?.item,
      conversationId: Office.context?.mailbox?.item?.conversationId ?? 'undefined',
      itemId: Office.context?.mailbox?.item?.itemId ?? 'undefined',
      internetMessageId: (Office.context?.mailbox?.item as any)?.internetMessageId ?? 'undefined',
    });
    if (window.Office && Office.context && Office.context.mailbox && Office.context.mailbox.item) {
      log('[itemChanged] updating mailbox item and resolving email');
      setMailboxItem(Office.context?.mailbox?.item);
      setThreadId(convertToUrlSafeBase64(Office.context?.mailbox?.item?.conversationId));
      resolveEmailFromMailbox();
    } else {
      logError('[itemChanged] handler fired but prerequisites missing, skipping update', {
        hasOffice: !!window.Office,
        hasContext: !!Office.context,
        hasMailbox: !!Office.context?.mailbox,
        hasItem: !!Office.context?.mailbox?.item,
      });
    }
  };

  const registerHandler = () => {
    log('[registerHandler] attempting to register ItemChanged handler', {
      hasOffice: !!window.Office,
      hasContext: !!Office.context,
      hasMailbox: !!Office.context?.mailbox,
      hasEventType: !!Office.EventType,
    });
    if (window.Office && Office.context && Office.context.mailbox && Office.EventType) {
      Office?.context?.mailbox?.addHandlerAsync(Office.EventType.ItemChanged, itemChangedHandler);
      log('[registerHandler] ItemChanged handler registered');
    } else {
      logError('[registerHandler] could not register handler - missing prerequisites');
    }
  };

  const initializedRef = useRef(false);
  const unmountedRef = useRef(false);
  const pollingIntervalsRef = useRef<ReturnType<typeof setInterval>[]>([]);

  const initializeFromOffice = (source: string) => {
    if (unmountedRef.current) {
      log(`[initialize] component already unmounted, ignoring call from ${source}`);
      return;
    }

    const hasMailbox = !!Office.context?.mailbox;

    if (initializedRef.current && hasMailbox) {
      log(`[initialize] already fully initialized, ignoring duplicate call from ${source}`);
      return;
    }

    if (initializedRef.current && !hasMailbox) {
      log(
        `[initialize] already initialized but mailbox still missing, ignoring call from ${source} (waiting for mailbox via polling/ItemChanged)`
      );
      return;
    }

    if (!hasMailbox) {
      log(
        `[initialize] called from ${source} but mailbox not available yet, marking ready but deferring mailbox setup`,
        {
          hasContext: !!Office.context,
          hasMailbox: false,
        }
      );
      setIsOfficeReady(true);
      registerHandler();
      return;
    }

    initializedRef.current = true;

    log(`[initialize] setting up Office state (via ${source})`, {
      hasContext: !!Office.context,
      hasMailbox: true,
      hasItem: !!Office.context?.mailbox?.item,
      conversationId: Office.context?.mailbox?.item?.conversationId ?? 'undefined',
      itemId: Office.context?.mailbox?.item?.itemId ?? 'undefined',
      internetMessageId: (Office.context?.mailbox?.item as any)?.internetMessageId ?? 'undefined',
      emailAddress: Office.context?.mailbox?.userProfile?.emailAddress ?? 'undefined',
      ewsUrl: Office.context?.mailbox?.ewsUrl ?? 'undefined',
      diagnosticsHost: (Office.context?.mailbox as any)?.diagnostics?.hostName ?? 'undefined',
      diagnosticsVersion: (Office.context?.mailbox as any)?.diagnostics?.hostVersion ?? 'undefined',
    });
    setIsOfficeReady(true);
    setMailboxItem(Office.context?.mailbox?.item);
    setThreadId(convertToUrlSafeBase64(Office.context?.mailbox?.item?.conversationId));
    registerHandler();
    resolveEmailFromMailbox();
  };

  const tryInitWithAllStrategies = (prefix: string) => {
    // Strategy 1: Office.onReady (recommended by Microsoft)
    log(`[${prefix}] calling Office.onReady()`, {
      typeofOffice: typeof Office,
      officeKeys: (() => {
        try {
          return Object.keys(Office).slice(0, 30);
        } catch {
          return 'inaccessible';
        }
      })(),
      hasOnReady: typeof Office.onReady === 'function',
    });
    const onReadyPromise = Office.onReady((info: any) => {
      if (unmountedRef.current) {
        log(`[${prefix}] Office.onReady callback fired but component already unmounted, ignoring`);
        return;
      }
      log(`[${prefix}] Office.onReady callback fired`, {
        host: info?.host ?? 'unknown',
        platform: info?.platform ?? 'unknown',
      });
      initializeFromOffice(`${prefix}/onReady`);
    });
    // Catch rejected promise — on some Classic Desktop builds onReady rejects silently
    if (onReadyPromise && typeof onReadyPromise.catch === 'function') {
      onReadyPromise.catch((err: unknown) => {
        logError(`[${prefix}] Office.onReady() promise REJECTED`, {
          error: String(err),
          errorType: typeof err,
          hasContext: !!Office.context,
          hasMailbox: !!Office.context?.mailbox,
        });
      });
    }

    // Strategy 2: Office.initialize as fallback — uses a different internal
    // mechanism than onReady. On some Outlook Classic Win32 builds, onReady
    // silently fails but initialize still fires.
    if (!Office.initialize) {
      log(`[${prefix}] registering Office.initialize fallback handler`);
      Office.initialize = () => {
        if (unmountedRef.current) {
          log(`[${prefix}] Office.initialize fired but component already unmounted, ignoring`);
          return;
        }
        log(`[${prefix}] Office.initialize fired`);
        initializeFromOffice(`${prefix}/initialize`);
      };
    } else {
      log(`[${prefix}] Office.initialize already assigned, skipping fallback handler`);
    }

    // Strategy 3: Poll for Office.context.mailbox. On Outlook Classic Win32,
    // both onReady and initialize can fail when the host pre-initialized
    // Office.js or stale sessionStorage hostInfoValue interferes. In that
    // case the context/mailbox may already exist or appear shortly.
    //
    // Also listen for raw postMessage from the Outlook host — if the host is
    // sending messages that Office.js isn't processing, we'll see them here.
    let hostMessageCount = 0;
    const messageListener = (event: MessageEvent) => {
      hostMessageCount++;
      // Only log first 5 to avoid flooding
      if (hostMessageCount <= 5) {
        log(`[${prefix}] raw postMessage received`, {
          count: hostMessageCount,
          origin: event.origin,
          dataType: typeof event.data,
          dataPreview: (() => {
            try {
              const str = typeof event.data === 'string' ? event.data : JSON.stringify(event.data);
              return str?.substring(0, 300) ?? 'empty';
            } catch {
              return 'unserializable';
            }
          })(),
        });
      }
    };
    window.addEventListener('message', messageListener);
    log(`[${prefix}] starting fallback polling for Office.context.mailbox`);
    let fallbackTicks = 0;
    const fallbackInterval = setInterval(() => {
      fallbackTicks++;
      if (unmountedRef.current) {
        log(`[${prefix}] fallback polling stopped (component unmounted)`, { tick: fallbackTicks });
        clearInterval(fallbackInterval);
        window.removeEventListener('message', messageListener);
        return;
      }
      if (initializedRef.current) {
        log(`[${prefix}] fallback polling stopped (already fully initialized)`, { tick: fallbackTicks });
        clearInterval(fallbackInterval);
        window.removeEventListener('message', messageListener);
        return;
      }
      log(`[${prefix}] fallback polling tick`, {
        tick: fallbackTicks,
        hasContext: !!Office.context,
        hasMailbox: !!Office.context?.mailbox,
        hasItem: !!Office.context?.mailbox?.item,
      });
      if (Office.context?.mailbox) {
        log(`[${prefix}] fallback polling found Office.context.mailbox`, {
          tick: fallbackTicks,
          hasItem: !!Office.context?.mailbox?.item,
          emailAddress: Office.context?.mailbox?.userProfile?.emailAddress ?? 'undefined',
          hostMessagesReceived: hostMessageCount,
        });
        clearInterval(fallbackInterval);
        window.removeEventListener('message', messageListener);
        initializeFromOffice(`${prefix}/fallback-poll`);
        return;
      }
      if (fallbackTicks >= 15) {
        // Deep diagnostic snapshot — captures internal Office.js state to
        // understand where the host↔webview handshake stalled.
        const diagSnapshot: Record<string, unknown> = {
          ticks: fallbackTicks,
          hasContext: !!Office.context,
          hasMailbox: !!Office.context?.mailbox,
          sessionStorageKeys: (() => {
            try {
              return Object.keys(sessionStorage);
            } catch {
              return 'inaccessible';
            }
          })(),
          hostInfoValue: (() => {
            try {
              return sessionStorage.getItem('hostInfoValue') ?? 'null';
            } catch {
              return 'inaccessible';
            }
          })(),
          // Probe 1: What keys ARE on Office.context when mailbox is missing?
          officeContextKeys: (() => {
            try {
              return Office.context ? Object.keys(Office.context).slice(0, 30) : 'context is falsy';
            } catch {
              return 'inaccessible';
            }
          })(),
          officeContextProto: (() => {
            try {
              return Office.context
                ? Object.getOwnPropertyNames(Object.getPrototypeOf(Office.context)).slice(0, 30)
                : 'context is falsy';
            } catch {
              return 'inaccessible';
            }
          })(),
          // Probe 2: OSF internals — the internal framework that Office.js uses
          hasOSF: typeof (window as any).OSF !== 'undefined',
          hasOSFAppFactory: typeof (window as any).OSF?._OfficeAppFactory !== 'undefined',
          osfAppFactoryKeys: (() => {
            try {
              const factory = (window as any).OSF?._OfficeAppFactory;
              return factory ? Object.keys(factory).slice(0, 30) : 'no factory';
            } catch {
              return 'inaccessible';
            }
          })(),
          // Check if the internal Office app context has been created
          osfGetContext: (() => {
            try {
              const factory = (window as any).OSF?._OfficeAppFactory;
              if (factory?.getContext) {
                const ctx = factory.getContext();
                return ctx
                  ? {
                      keys: Object.keys(ctx).slice(0, 20),
                      hasAppOM: !!ctx._appOM,
                      appOMType: ctx._appOM ? typeof ctx._appOM : 'missing',
                    }
                  : 'getContext returned null';
              }
              return 'no getContext method';
            } catch (e) {
              return `error: ${String(e)}`;
            }
          })(),
          // Probe 3: Check Office.context.requirements and platform info
          hasRequirements: !!Office.context?.requirements,
          hasUI: !!(Office.context as any)?.ui,
          hasDiagnostics: !!(Office.context as any)?.diagnostics,
          diagnosticsInfo: (() => {
            try {
              const diag = (Office.context as any)?.diagnostics;
              return diag
                ? { host: diag.hostName, version: diag.hostVersion, platform: diag.platform }
                : 'no diagnostics';
            } catch {
              return 'inaccessible';
            }
          })(),
          // Probe 4: Check if Office.context.mailbox is a getter that throws
          mailboxAccessError: (() => {
            try {
              const mb = Office.context.mailbox;
              return mb === undefined ? 'undefined' : mb === null ? 'null' : 'has value';
            } catch (e) {
              return `throws: ${String(e)}`;
            }
          })(),
        };
        diagSnapshot.hostMessagesReceived = hostMessageCount;
        logError(`[${prefix}] fallback polling gave up — deep diagnostic`, diagSnapshot);
        clearInterval(fallbackInterval);
        window.removeEventListener('message', messageListener);
      }
    }, 1000);
    pollingIntervalsRef.current.push(fallbackInterval);
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      console.warn('[OfficeContext][mount] SSR detected, skipping Office initialization');
      return;
    }

    log('[mount] OfficeProvider useEffect running', {
      hasWindowOffice: !!window.Office,
      hasOnReady: !!(window.Office && 'onReady' in Office),
      hasContext: !!(window.Office && Office.context),
      hasMailbox: !!(window.Office && Office.context?.mailbox),
      hasItem: !!(window.Office && Office.context?.mailbox?.item),
      userAgent: navigator.userAgent,
      href: window.location.href,
      hostInfoValue: (() => {
        try {
          return sessionStorage.getItem('hostInfoValue') ?? 'null';
        } catch {
          return 'inaccessible';
        }
      })(),
    });

    if (!window._historyCache) {
      log('[mount] caching history.replaceState and history.pushState');
      window._historyCache = {
        replaceState: window.history.replaceState.bind(window.history),
        pushState: window.history.pushState.bind(window.history),
      };
    } else {
      log('[mount] history cache already exists, skipping');
    }

    if (window.Office) {
      if (Office.context?.mailbox) {
        log('[mount] Office.context.mailbox already exists, initializing directly');
        initializeFromOffice('mount/immediate');
      } else if ('onReady' in Office) {
        tryInitWithAllStrategies('mount');
      } else {
        logError('[mount] window.Office exists but no onReady and no context.mailbox', {
          officeKeys: Object.keys(Office).slice(0, 20),
        });
      }
    } else {
      log('[mount] window.Office not available yet, waiting for Script tag to load');
    }
  }, []);

  const handleScriptLoad = () => {
    log('[scriptLoad] Office.js script loaded', {
      hasWindowOffice: !!window.Office,
      hasContext: !!(window.Office && Office.context),
      hasMailbox: !!(window.Office && Office.context?.mailbox),
      hasHistoryCache: !!window._historyCache,
      sessionStorageKeys: (() => {
        try {
          return Object.keys(sessionStorage);
        } catch {
          return 'inaccessible';
        }
      })(),
      hostInfoValue: (() => {
        try {
          return sessionStorage.getItem('hostInfoValue') ?? 'null';
        } catch {
          return 'inaccessible';
        }
      })(),
    });
    if (window._historyCache) {
      window.history.replaceState = window._historyCache.replaceState;
      window.history.pushState = window._historyCache.pushState;
      log('[scriptLoad] restored history.replaceState and history.pushState from cache');
    } else {
      log('[scriptLoad] no history cache found, skipping history restore');
    }

    if (Office.context?.mailbox) {
      log('[scriptLoad] Office.context.mailbox already exists, initializing directly');
      initializeFromOffice('scriptLoad/immediate');
      return;
    }

    log('[scriptLoad] Office.context.mailbox not yet available, trying all strategies', {
      hasContext: !!Office.context,
    });
    tryInitWithAllStrategies('scriptLoad');
  };

  const handleScriptError = (e: any) => {
    logError('[scriptError] Office.js script failed to load', {
      error: String(e),
      hasWindowOffice: !!window.Office,
      hasContext: !!(window.Office && Office.context),
      hasMailbox: !!(window.Office && Office.context?.mailbox),
    });
    if (window._historyCache) {
      window.history.replaceState = window._historyCache.replaceState;
      window.history.pushState = window._historyCache.pushState;
      logError('[scriptError] restored history from cache after script error');
    }
    if (!window.Office || !window.Office.context?.mailbox) {
      logError('[scriptError] resetting all Office state to undefined', {
        hasWindowOffice: !!window.Office,
        hasMailbox: !!(window.Office && Office.context?.mailbox),
      });
      setIsOfficeReady(false);
      setMailboxItem(undefined);
      setThreadId(undefined);
      setEmail(undefined);
    } else {
      log('[scriptError] script failed but Office context still available, keeping state');
    }
  };

  useEffect(() => {
    log('[watchdog] watchdog useEffect running', { isOfficeReady });
    let ticks = 0;
    const MAX_TICKS = 12;
    const interval = setInterval(() => {
      ticks++;
      if (isOfficeReady) {
        log('[watchdog] Office is ready, stopping watchdog', { tick: ticks });
        clearInterval(interval);
      } else if (ticks <= MAX_TICKS) {
        logError('[watchdog] still not ready after mount', {
          isOfficeReady,
          tick: ticks,
          hasWindowOffice: !!window.Office,
          hasOnReady: !!(window.Office && 'onReady' in Office),
          hasContext: !!(window.Office && Office.context),
          hasMailbox: !!(window.Office && Office.context?.mailbox),
          hasItem: !!(window.Office && Office.context?.mailbox?.item),
          initializedRefCurrent: initializedRef.current,
          hostInfoValue: (() => {
            try {
              return sessionStorage.getItem('hostInfoValue') ?? 'null';
            } catch {
              return 'inaccessible';
            }
          })(),
        });
      } else {
        logError('[watchdog] giving up, Office never became ready', {
          totalSeconds: ticks * 5,
          hasWindowOffice: !!window.Office,
          hasContext: !!(window.Office && Office.context),
          hasMailbox: !!(window.Office && Office.context?.mailbox),
          initializedRefCurrent: initializedRef.current,
          hostInfoValue: (() => {
            try {
              return sessionStorage.getItem('hostInfoValue') ?? 'null';
            } catch {
              return 'inaccessible';
            }
          })(),
        });
        clearInterval(interval);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isOfficeReady]);

  // Timeout: after OFFICE_INIT_TIMEOUT_MS, show error page if Office is still not ready.
  // When isOfficeReady becomes true, the effect re-runs and bails out, clearing the timer.
  useEffect(() => {
    if (isOfficeReady) return;

    const timer = setTimeout(() => {
      if (!unmountedRef.current) {
        logError('[timeout] Office JS initialization timed out', {
          timeoutMs: OFFICE_INIT_TIMEOUT_MS,
          hasWindowOffice: !!window.Office,
          hasContext: !!(window.Office && Office.context),
          hasMailbox: !!(window.Office && Office.context?.mailbox),
        });
        setIsOfficeTimedOut(true);
      }
    }, OFFICE_INIT_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [isOfficeReady]);

  useEffect(() => {
    return () => {
      unmountedRef.current = true;

      log('[cleanup] OfficeProvider unmounting, clearing polling intervals', {
        pollingIntervalCount: pollingIntervalsRef.current.length,
      });
      pollingIntervalsRef.current.forEach((id) => clearInterval(id));
      pollingIntervalsRef.current = [];

      if (window.Office && Office.context && Office.context.mailbox && Office.EventType) {
        log('[cleanup] removing ItemChanged handler');
        Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged);
      } else {
        log('[cleanup] unmounting but no handler to remove', {
          hasOffice: !!window.Office,
          hasContext: !!(window.Office && Office.context),
          hasMailbox: !!(window.Office && Office.context?.mailbox),
          hasEventType: !!(window.Office && Office.EventType),
        });
      }
    };
  }, []);

  function convertToUrlSafeBase64(value: string | undefined): string | undefined {
    return value ? value.replace(/\+/g, '_').replace(/\//g, '-') : undefined;
  }

  useEffect(() => {
    if (typeof window === 'undefined' || window.Office) return;

    const script = document.createElement('script');
    script.id = 'office-js-loader';
    script.src = 'https://appsforoffice.microsoft.com/lib/1.1/hosted/office.js';
    script.async = true;
    script.onload = handleScriptLoad;
    script.onerror = handleScriptError;
    document.head.appendChild(script);
  }, []);

  log('[render] OfficeProvider rendering', {
    isOfficeReady,
    hasEmail: !!email,
    email: email ?? 'undefined',
    hasThreadId: !!threadId,
    threadId: threadId ?? 'undefined',
    hasMailboxItem: !!mailboxItem,
  });

  return (
    <>
      <OfficeContext.Provider value={{ isOfficeReady, mailboxItem, threadId, email }}>
        {isOfficeReady ? (
          children
        ) : isOfficeTimedOut ? (
          <OfficeConnectionError onRetry={() => window.location.reload()} />
        ) : (
          <LoadingPage testId="outlook-office-loading" />
        )}
      </OfficeContext.Provider>
    </>
  );
};
